import React, { useMemo } from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { STATUS_COLORS, CHART_INK, formatMonthLabel } from './chartUtils';

// Generic monthly stacked-status chart. `monthlyData` items must have
// `{year, month, count, ...one field per statusGroups[].key}`.
// `statusGroups`: [{ key, label, color, icon }] — any remainder
// (count - sum of named groups) is rendered as a catch-all "other" bucket
// when `otherLabel` is supplied.
const StatusBreakdownChart = ({ monthlyData, expanded, statusGroups, otherLabel, totalLabel = 'סה״כ בטווח שנבחר', height: customHeight }) => {
    const data = useMemo(
        () =>
            (monthlyData || []).map((m) => {
                const row = { label: formatMonthLabel(m.year, m.month) };
                let named = 0;
                statusGroups.forEach((g) => {
                    row[g.key] = m[g.key] || 0;
                    named += row[g.key];
                });
                if (otherLabel) row.other = Math.max((m.count || 0) - named, 0);
                return row;
            }),
        [monthlyData, statusGroups, otherLabel],
    );

    const totals = useMemo(() => {
        const perGroup = statusGroups.map((g) => ({
            ...g,
            total: data.reduce((sum, m) => sum + (m[g.key] || 0), 0),
        }));
        const other = otherLabel ? data.reduce((sum, m) => sum + (m.other || 0), 0) : 0;
        const grandTotal = perGroup.reduce((sum, g) => sum + g.total, 0) + other;
        return { perGroup, other, grandTotal };
    }, [data, statusGroups, otherLabel]);

    const height = customHeight ?? (expanded ? 300 : 190);

    if (!data.length) {
        return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>אין נתונים לטווח שנבחר</Typography>;
    }

    return (
        <Box>
            <Stack direction="row" gap={1} mb={1} flexWrap="wrap">
                {totals.perGroup.map((g) => {
                    const GroupIcon = g.icon;
                    const rate = totals.grandTotal > 0 ? Math.round((g.total / totals.grandTotal) * 100) : 0;
                    return (
                        <Chip
                            key={g.key}
                            icon={GroupIcon ? <GroupIcon sx={{ color: `${g.color} !important` }} /> : undefined}
                            label={`${g.total} ${g.label} (${rate}%)`}
                            size="small"
                            variant="outlined"
                        />
                    );
                })}
                {otherLabel && totals.other > 0 && (
                    <Chip
                        icon={<HourglassEmptyIcon sx={{ color: `${STATUS_COLORS.warning} !important` }} />}
                        label={`${totals.other} ${otherLabel}`}
                        size="small"
                        variant="outlined"
                    />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {totalLabel}: {totals.grandTotal.toLocaleString('he-IL')}
                </Typography>
            </Stack>

            <ResponsiveContainer width="100%" height={height}>
                <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
                    <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Legend verticalAlign="top" height={28} iconType="circle" />
                    {statusGroups.map((g, i) => (
                        <Bar
                            key={g.key}
                            dataKey={g.key}
                            name={g.label}
                            stackId="status"
                            fill={g.color}
                            radius={!otherLabel && i === statusGroups.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                            maxBarSize={30}
                        />
                    ))}
                    {otherLabel && (
                        <Bar dataKey="other" name={otherLabel} stackId="status" fill={STATUS_COLORS.warning} radius={[4, 4, 0, 0]} maxBarSize={30} />
                    )}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default StatusBreakdownChart;
